import React, { useEffect, useState } from "react";
import { Box, Button, Typography, Stack } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import DetailsSidePanel from "./DetailsSidePanel";
import useDebounce from "../hooks/useDebounce";
import axiosInstance from "../utils/axiosConfig";

import { convertTo24HourFormat } from "../utils/convertTimestampTo12HourFormat";
import SearchActionComponent from "./reusableComponents/SearchActionComponent";
import SwipeableDrawerComponent from "./SwipeableDrawerComponent ";
import { BootstrapTooltip } from "./DetailsSidePanelChart";
import usePermission from "../hooks/usePermission";

import {
  customeDateHandler,
  customeTimeHandler,
} from "../const/commonfilterobject";
import CustomFilter, {
  SortPanel,
} from "./reusableComponents/Filter/CustomFilter";
import FilteredResults from "./FilteredResults";
import { showErrorToast } from "../utils/toastUtils";
import { removeQuery } from "../utils/queryHelpers";
import DataTable from "./DataTable";
import { bookingIdObj } from "../const/sort";
import useQueryParams from "../hooks/useQueryParams ";
import dayjs from "dayjs";
import CommonTableHead from "./CommonTableHead";
import { notifyToast } from "../utils/notify";
import { useSettings } from "../hooks/useSettings";

const StatusIndicator = ({ index, text, fill }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
    }}
  >
    <svg
      style={{
        borderRadius: "50%",
        ...(index === 0 && {
          boxShadow:
            "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
        }),
      }}
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <circle cx="10" cy="10" r="10" fill={fill} />
    </svg>

    <Typography>{text}</Typography>
  </Box>
);


const AdvanceBooking = () => {
  const [isLoading, setisLoading] = useState(false);
  const location = useLocation();
  const [trips, setTrips] = useState([]);

  const { searchParams, setSearchParams, getQuery } = useQueryParams();
  const permissions = usePermission();
  
  const [loader, setLoader] = useState(false);

  const toBoolean = (value) => {
    return value === "true";
  };

  const drawer = toBoolean(searchParams.get("drawer"));
  const id = searchParams.get("id");
  const [showDriverDetails, setDriverDetails] = useState({
    right: drawer || false,
  });
  const [driverId, setDriverId] = useState(id || "");
  const [showFilter, setShowFilter] = useState({ right: false });

  const rideStatusParam = searchParams.get("ride_status");
  const [rideStatus, setRideStatus] = useState(
    rideStatusParam ?? location?.state?.status ?? ""
  );

  const cabTypeParam = searchParams.get("cab_type");
  const [cabType, setCabType] = useState(cabTypeParam ?? "");

  const { dateformat, timezone, formatDate, timeFormat } = useSettings();

  const [bookingDay, setBookingDay] = useState(getQuery("booking_day") ?? "");
  const [date, setDate] = useState({
    startDate: getQuery("booking_date_gte")
      ? dayjs(getQuery("booking_date_gte"))
      : null,
    endDate: getQuery("booking_date_lte")
      ? dayjs(getQuery("booking_date_lte"))
      : null,
  });

  const [scheduledDay, setScheduledDay] = useState(getQuery("scheduled_day") ?? "");
  const [scheduledDate, setScheduledDate] = useState({
    startDate: getQuery("scheduled_date_gte")
      ? dayjs(getQuery("scheduled_date_gte"))
      : null,
    endDate: getQuery("scheduled_date_lte")
      ? dayjs(getQuery("scheduled_date_lte"))
      : null,
  });

  const [search, setSearch] = useState("");

  const [time, setTime] = useState({
    minTime: getQuery("booking_time_gte")
      ? dayjs(getQuery("booking_time_gte"))
      : "",
    maxTime: getQuery("booking_time_lte")
      ? dayjs(getQuery("booking_time_lte"))
      : "",
  });

  const [showSort, SetShowSort] = useState({ right: false });
  const [sort, setSort] = useState({
    bookingId: getQuery("booking_id_sort") ?? "",
  });

  // Debounce search
  const searchDebounce = useDebounce(search, 1500);

  // Pagination
  const page = parseInt(searchParams.get("page"), 0) || 0;
  const pageSize = parseInt(searchParams.get("limit"), 15) || 15;
  const [paginationModel, setPaginationModel] = React.useState({
    page: page || 0,
    pageSize: pageSize || 10,
  });

  const [totalCount, setTotalCount] = useState(0);
  const [activeButton, setActiveButton] = useState("Trips");

  const setIdQueryParam = (id) => {
    if (!id) return;
    searchParams.set("id", id);
    setSearchParams(searchParams, { replace: true });
  };

  const setDrawerQueryParam = (id) => {
    if (!id) return;
    searchParams.set("drawer", id);
    setSearchParams(searchParams, { replace: true });
  };

  const navigate = useNavigate();

  const toggleDrawer = (anchor, open) => (event) => {
    if (!open) {
      setDriverId("");
    }
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setDrawerQueryParam(open);
    setDriverDetails((prev) => ({ ...prev, [anchor]: open }));
  };

  const toggleFilterDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setShowFilter((prev) => ({ ...prev, [anchor]: open }));
  };

  const toggleSortDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    SetShowSort({ ...showSort, [anchor]: open });
  };

  const handleSelectNameClick = (name) => {
    setActiveButton(name);
  };

  // Columns Configuration
  const columns = [
    {
      field: "booking_id",
      headerName: "Booking ID",
      flex: 0.8,
      renderCell: (params) => {
        return (
          <Button
            id={params.id}
            sx={{
              color: "#043A87",
              fontFamily: "Proxima Nova",
              fontSize: "13px",
              letterSpacing: "0.26px",
              textTransform: "capitalize",
              border: "none",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!params?.id) {
                return;
              }
              const handleEvent = toggleDrawer("right", true);
              setDriverId(params?.id ?? "");
              setIdQueryParam(params?.id);
              handleEvent(e);
            }}
          >
            {params.row.booking_id}
          </Button>
        );
      },
    },
    {
      field: "scheduled_at",
      headerName: "Scheduled Date & Time",
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => {
        if (!params?.value) {
          return "--";
        }
        let date = formatDate(params.value, timezone, dateformat);
        const time = formatDate(params.value, timezone, timeFormat);
        return (
          <Stack direction={"row"} gap={"5px"} alignItems={"flex-end"}>
            <Typography
              sx={{
                color: "#1D1F2C",
                fontFamily: "Proxima Nova",
                fontSize: "13px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "normal",
                letterSpacing: "0.26px",
              }}
            >
              {date}
            </Typography>

            <Typography
              sx={{
                color: "#6F747E",
                fontFamily: "Proxima Nova",
                fontSize: "10px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "normal",
                letterSpacing: "0.26px",
              }}
            >
              {time}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: "status",
      headerName: "Ride Status",
      flex: 0.9,
      renderCell: (params) => {
        const status = params?.row?.status;
        if (!status) return "--";

        let label = status;
        let dotColor = "#D1D1D1";
        let hasBorder = false;

        switch (status) {
          case "SCHEDULED":
          case "UPCOMING":
            label = "Upcoming";
            dotColor = "#D1D1D1";
            hasBorder = true;
            break;
          case "REQUESTED":
            label = "Requested";
            dotColor = "#fff";
            hasBorder = true;
            break;
          case "ONGOING":
          case "ASSIGNED":
            label = status === "ONGOING" ? "Ongoing" : "Assigned";
            dotColor = "#043A87";
            break;
          case "COMPLETED":
            label = "Completed";
            dotColor = "#FFC940";
            break;
          case "CANCELLED":
            label = "Cancelled";
            dotColor = "#D1D1D1";
            break;
          case "EXPIRED":
          case "DRIVER_NOT_FOUND":
            label = status === "EXPIRED" ? "Expired" : "Driver Not Found";
            dotColor = "red";
            break;
          default:
            dotColor = "#D1D1D1";
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
              sx={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: dotColor,
                ...(hasBorder && {
                  boxShadow:
                    "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
                }),
              }}
            />
            <Typography
              sx={{
                color: "#1D1F2C",
                fontFamily: "Proxima Nova",
                fontSize: "13px",
                fontWeight: "500",
              }}
            >
              {label}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "driver_details.full_name",
      headerName: "Driver Name",
      flex: 1,
      renderCell: (params) => {
        const driver = params?.row?.driver_details;
        if (!driver || !driver.full_name) {
          return "--";
        }
        return (
          <Typography
            sx={{
              color: "#043A87",
              fontFamily: "Proxima Nova",
              fontSize: "13px",
              fontWeight: "500",
              textTransform: "capitalize",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" }
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (driver.id) {
                navigate(`/driver/${driver.id}`);
              }
            }}
          >
            {driver.full_name}
          </Typography>
        );
      },
    },
    {
      field: "driver_details.phone",
      headerName: "Driver Number",
      flex: 1,
      renderCell: (params) => {
        const driver = params?.row?.driver_details;
        if (!driver || !driver.phone) {
          return "--";
        }
        return (
          <Typography
            sx={{
              color: "#1D1F2C",
              fontFamily: "Proxima Nova",
              fontSize: "13px",
              fontWeight: "400",
            }}
          >
            {driver.phone}
          </Typography>
        );
      },
    },
    {
      field: "rider_details.full_name",
      headerName: "Passenger Name",
      flex: 0.9,
      renderCell: (params) => {
        const name = params?.row?.rider_details?.full_name || "--";
        return (
          <BootstrapTooltip title={name}>
            <Typography
              sx={{
                color: "#1D1F2C",
                fontFamily: "Proxima Nova",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "13px",
                textTransform: "capitalize",
                letterSpacing: "0.26px",
                fontWeight: "500",
              }}
            >
              {name}
            </Typography>
          </BootstrapTooltip>
        );
      },
    },
    {
      field: "payment_details.amount",
      headerName: "Trip Fare",
      flex: 0.8,
      renderCell: (params) => {
        const fare = params?.row?.payment_details?.amount || params?.row?.price_breakup?.total_fare || "--";
        const displayFare = fare !== "--" ? `₱${fare}` : "--";
        return (
          <Typography
            sx={{
              color: "#1D1F2C",
              fontFamily: "Proxima Nova",
              fontSize: "13px",
              fontWeight: "500",
            }}
          >
            {displayFare}
          </Typography>
        );
      },
    },
    {
      field: "created_at",
      headerName: "Booking Date",
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => {
        if (!params?.row?.created_at) {
          return "--";
        }
        return (
          <Typography
            sx={{
              color: "#1D1F2C",
              fontFamily: "Proxima Nova",
              fontSize: "13px",
              letterSpacing: "0.26px",
            }}
          >
            {formatDate(params?.row?.created_at, timezone, dateformat)}
          </Typography>
        );
      },
    },
    {
      field: "pickup_location.address",
      headerName: "Pickup Location",
      flex: 2,
      renderCell: (params) => {
        const address = params?.row?.pickup_location?.address || params?.row?.pickup_location?.address_text || "--";
        return (
          <BootstrapTooltip title={address}>
            <Typography
              sx={{
                color: "#1D1F2C",
                fontFamily: "Proxima Nova",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "13px",
                letterSpacing: "0.26px",
                fontWeight: "500",
                whiteSpace: "nowrap"
              }}
            >
              {address}
            </Typography>
          </BootstrapTooltip>
        );
      },
    },
    {
      field: "dropoff_location.address",
      headerName: "Drop-off Location",
      flex: 2,
      renderCell: (params) => {
        const address = params?.row?.dropoff_location?.address || params?.row?.dropoff_location?.address_text || "--";
        return (
          <BootstrapTooltip title={address}>
            <Typography
              sx={{
                color: "#1D1F2C",
                fontFamily: "Proxima Nova",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "13px",
                letterSpacing: "0.26px",
                fontWeight: "500",
                whiteSpace: "nowrap"
              }}
            >
              {address}
            </Typography>
          </BootstrapTooltip>
        );
      },
    },
  ];

  const checkQuery = (download = false) => {
    const query = [];

    if (download) {
      query.push(`is_download=${true}`);
    }

    if (rideStatus !== "") {
      query.push(`ride_status=${rideStatus}`);
    }

    if (cabType !== "") {
      query.push(`cab_type=${cabType}`);
    }

    if (bookingDay !== "" && bookingDay !== "Recent") {
      query.push(`booking_day=${bookingDay}`);
    }

    if (scheduledDay !== "") {
      query.push(`scheduled_day=${scheduledDay}`);
    }

    if (searchDebounce && search) {
      query.push(`search=${searchDebounce}`);
    }

    if (date.startDate && date.endDate) {
      let startDate = `${date.startDate.year()}-${
        date.startDate.month() + 1
      }-${date.startDate.date()}`;
      let endDate = `${date.endDate.year()}-${
        date.endDate.month() + 1
      }-${date.endDate.date()}`;
      query.push(`date_gte=${startDate}&date_lte=${endDate}`);
    }

    if (scheduledDate.startDate && scheduledDate.endDate) {
      let startDate = `${scheduledDate.startDate.year()}-${
        scheduledDate.startDate.month() + 1
      }-${scheduledDate.startDate.date()}`;
      let endDate = `${scheduledDate.endDate.year()}-${
        scheduledDate.endDate.month() + 1
      }-${scheduledDate.endDate.date()}`;
      query.push(`scheduled_gte=${startDate}&scheduled_lte=${endDate}`);
    }

    if (time.minTime && time.maxTime) {
      query.push(
        `min_booking_time=${convertTo24HourFormat(
          time.minTime
        )}&max_booking_time=${convertTo24HourFormat(time.maxTime)}`
      );
    }

    if (sort?.bookingId) {
      query.push(`booking_id_sort=${sort?.bookingId}`);
    }

    return query.join("&");
  };

  const getTrips = async (qurys = "") => {
    setisLoading(true);
    let url = `/crm/scheduled-rides/?page=${paginationModel.page + 1}&limit=${
      paginationModel.pageSize
    }`;
    if (qurys) {
      url += `&${qurys}`;
    }

    try {
      await axiosInstance.get(url).then((res) => {
        setLoader(false);
        setTotalCount(res.data.data.total_count);
        setTrips(res?.data?.data?.records ?? []);
      });
    } catch (error) {
      console.log("Error while Fetching Scheduled Rides", error);
    } finally {
      setisLoading(false);
    }
  };

  const downloadFile = async () => {
    let qurys = checkQuery(true);
    let url = `/crm/scheduled-rides/?page=${paginationModel.page + 1}&limit=${
      paginationModel.pageSize
    }`;
    if (qurys) {
      url += `&${qurys}`;
    }

    try {
      await axiosInstance.get(url).then(() => {
        notifyToast.success(
          "Success",
          "The file has been successfully sent to your email. Please check your inbox.",
          {
            autoClose: 5000,
          }
        );
      });
    } catch (error) {
      let message = error?.response?.data?.message;
      if (message) {
        showErrorToast(message);
      }
      console.log("Error while exporting Scheduled Rides", error);
    }
  };

  const RideStatusConfig = [
    { text: "First Mile", fill: "#fff" },
    { text: "Ongoing Ride", fill: "#1D1F2C" },
    { text: "Completed Ride", fill: "#FFC940" },
    { text: "Cancelled Ride", fill: "#D1D1D1" },
    { text: "Driver Not Found", fill: "red" },
  ];

  const allowed_sub_sections = permissions?.SUB_SECTIONS ?? ["trips"];
  const sub_section_to_label = { trips: "Trips" };

  const tableHeadButtonConfig = allowed_sub_sections?.map((sub_section) => {
    return {
      label: sub_section_to_label[sub_section],
      value: "Trips",
      totalCount,
      onClick: handleSelectNameClick,
    };
  });

  const filterOptions = [
    {
      summryText: "Ride Status",
      state: rideStatus,
      filterName: "ride_status",
      setState: setRideStatus,
      options: [
        { label: "Upcoming", value: "SCHEDULED" },
        { label: "Requested", value: "REQUESTED" },
        { label: "Ongoing", value: "ONGOING" },
        { label: "Completed", value: "COMPLETED" },
        { label: "Cancelled", value: "CANCELLED" },
        { label: "Driver Not Found", value: "DRIVER_NOT_FOUND" },
        // { label: "Live Trips", value: "LIVE_TRIP" },
      ],
    },
    {
      summryText: "Cab Type",
      filterName: "cab_type",
      state: cabType,
      setState: setCabType,
      options: [
        { value: "COMPACT", label: "Compact" },
        { value: "TAXI", label: "Taxi" },
        { value: "TAXI_EV", label: "Taxi EV" },
        { value: "PLUS", label: "Plus" },
        { value: "EXECUTIVE", label: "Executive" },
        { value: "PEEKUP_BLUE", label: "Peekup Blue" },
        { value: "PEEKUP_BLUE_XL", label: "Peekup Blue XL" },
      ],
    },
    {
      summryText: "Scheduled Day",
      filterName: "scheduled_day",
      state: scheduledDay,
      setState: setScheduledDay,
      options: [
        { label: "Today", value: "today" },
        { label: "Tomorrow", value: "tomorrow" },
        { label: "Next 3 Days", value: "next_3_days" },
        { label: "Next 7 Days", value: "next_7_days" },
      ],
      date: {
        customDateState: scheduledDate,
        setState: setScheduledDate,
        customeDateHandler,
        customDateHeader: "Custom Date Range",
        filterName: ["scheduled_date_gte", "scheduled_date_lte"],
        customDateLabel: ["From", "To"],
        customDateName: ["startDate", "endDate"],
      },
    },
    {
      summryText: "Booking Day",
      filterName: "booking_day",
      state: bookingDay,
      setState: setBookingDay,
      options: [
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "Last week", value: "last_week" },
        { label: "Last month", value: "last_month" },
        { label: "Last year", value: "last_year" },
      ],
      date: {
        customDateState: date,
        setState: setDate,
        customeDateHandler,
        customDateHeader: "Custom",
        filterName: ["booking_date_gte", "booking_date_lte"],
        customDateLabel: ["From", "To"],
        customDateName: ["startDate", "endDate"],
      },
    },
    {
      summryText: "Booking time",
      time: {
        timeLabel: ["Min Time", "Max Time"],
        filterName: ["booking_time_gte", "booking_time_lte"],
        customTimeName: ["minTime", "maxTime"],
        timeState: time,
        setState: setTime,
        customeTimeHandler,
      },
    },
  ];

  const sortFilterConfig = {
    options: [
      {
        ...bookingIdObj,
        state: sort?.bookingId ?? [],
        filterName: "booking_id_sort",
      },
    ],
    handleClearState: () => {},
  };

  const buttonConfig = {
    buttonConfig: tableHeadButtonConfig,
    active: activeButton,
    totalcount: totalCount,
  };

  useEffect(() => {
    let qurys = checkQuery();
    getTrips(qurys);
  }, [
    rideStatus,
    cabType,
    bookingDay,
    scheduledDay,
    date,
    scheduledDate,
    paginationModel.page,
    paginationModel.pageSize,
    searchDebounce,
    time.minTime,
    time.maxTime,
    sort,
  ]);

  return (
    <>
      {loader ? (
        <div>loading..</div>
      ) : (
        <Box sx={{ margin: "20px" }}>
          <SearchActionComponent
            value={search}
            setState={setSearch}
            toggleDrawer={toggleFilterDrawer}
            placeholder={"Search Customer , Driver, Booking ID"}
            toggleSortDrawer={toggleSortDrawer}
            require={{
              search: true,
              filter: true,
              download: true,
              sort: false,
            }}
            downloadFile={downloadFile}
          />

          <FilteredResults options={filterOptions} />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <CommonTableHead
              totalcount={buttonConfig?.totalcount}
              ButtonConfig={tableHeadButtonConfig}
              active={buttonConfig?.active}
            />
          </Box>

          <DataTable
            row={trips || []}
            columns={columns}
            sx={{ cursor: "pointer" }}
            paginationModel={paginationModel}
            loading={isLoading}
            setPaginationModel={setPaginationModel}
            totalCount={Math.ceil(totalCount)}
          />

          {showDriverDetails["right"] && (
            <SwipeableDrawerComponent
              isOpen={showDriverDetails["right"]}
              anchor="right"
              onClose={(e) => {
                removeQuery("drawer", setSearchParams);
                removeQuery("id", setSearchParams);
                toggleDrawer("right", false)(e);
              }}
            >
              <DetailsSidePanel
                setDriverId={setDriverId}
                DId={driverId}
                anchor={"right"}
                role=""
                specialFilter="driver_ticket"
                toggleDrawer={toggleDrawer}
              />
            </SwipeableDrawerComponent>
          )}

          {showFilter && (
            <SwipeableDrawerComponent
              isOpen={showFilter["right"]}
              anchor="right"
              onClose={toggleFilterDrawer("right", false)}
            >
              <CustomFilter
                filterOptions={filterOptions}
                toggleDrawer={() => toggleFilterDrawer("right", false)}
              />
            </SwipeableDrawerComponent>
          )}

          {showSort.right && (
            <SwipeableDrawerComponent
              isOpen={showSort["right"]}
              onClose={toggleSortDrawer("right", false)}
              anchor="right"
            >
              <SortPanel
                toggleDrawer={toggleSortDrawer}
                sortOptions={sortFilterConfig?.options}
                setState={setSort}
              />
            </SwipeableDrawerComponent>
          )}

          <Box
            sx={{
              width: "auto",
              margin: "auto",
              padding: "25px 0",
            }}
          >
            <Typography>Ride Status</Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginTop: "12px",
              }}
            >
              {RideStatusConfig.length > 0 &&
                RideStatusConfig.map((res, index) => (
                  <StatusIndicator
                    key={res.text}
                    index={index}
                    text={res.text}
                    fill={res.fill}
                  />
                ))}
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AdvanceBooking;
